import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { Class3CalloutPairings } from '../features/callout/components/Class3CalloutPairings';
import { Class4CalloutPairings } from '../features/callout/components/Class4CalloutPairings';
import { Class5CalloutPairings } from '../features/callout/components/Class5CalloutPairings';
import { Cadet } from '../shared/types/scoreboard.types';
import { Scoreboard } from './Scoreboard';
import { ExcelService } from '../services/excel/excel.service';
import { FILE_PATHS, UI_CONSTANTS } from '../shared/constants';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { Class3CalloutUtils } from '../features/callout/utils/class3-callout.utils';
import { Class4CalloutUtils } from '../features/callout/utils/class4-callout.utils';
import { Class5CalloutUtils } from '../features/callout/utils/class5-callout.utils';
import { CalloutPair } from '../features/callout/types';

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

// Memoized TabPanel component
const TabPanel = React.memo<TabPanelProps>(({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`scoreboard-tabpanel-${index}`}
    aria-labelledby={`scoreboard-tab-${index}`}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
));

// Custom hook for data management with callout processing
const useScoreboardData = () => {
  const [c2ScoreboardData, setC2ScoreboardData] = useState<Cadet[]>([]);
  const [c3BeforeCalloutData, setC3BeforeCalloutData] = useState<Cadet[]>([]);
  const [c3AfterCalloutData, setC3AfterCalloutData] = useState<Cadet[]>([]);
  const [c4BeforeCalloutData, setC4BeforeCalloutData] = useState<Cadet[]>([]);
  const [c4AfterCalloutData, setC4AfterCalloutData] = useState<Cadet[]>([]);
  const [c5BeforeCalloutData, setC5BeforeCalloutData] = useState<Cadet[]>([]);
  const [class3CalloutPairs, setClass3CalloutPairs] = useState<CalloutPair[]>([]);
  const [class4CalloutPairs, setClass4CalloutPairs] = useState<CalloutPair[]>([]);
  const [class5CalloutPairs, setClass5CalloutPairs] = useState<CalloutPair[]>([]);
  const [c5AfterCalloutData, setC5AfterCalloutData] = useState<Cadet[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableClasses, setAvailableClasses] = useState<{
    hasClass3Data: boolean;
    hasClass4Data: boolean;
    hasClass5Data: boolean;
  }>({ hasClass3Data: false, hasClass4Data: false, hasClass5Data: false });

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load only available files - handle missing files gracefully
      const loadFilesSafely = async () => {
        const results: {
          c2Cadets: Cadet[];
          c3BeforeCallout: Cadet[];
          c3AfterCallout: Cadet[];
          c4BeforeCallout: Cadet[];
          c5BeforeCallout: Cadet[];
          hasClass3Data: boolean;
          hasClass4Data: boolean;
          hasClass5Data: boolean;
        } = {
          c2Cadets: [],
          c3BeforeCallout: [],
          c3AfterCallout: [],
          c4BeforeCallout: [],
          c5BeforeCallout: [],
          hasClass3Data: false,
          hasClass4Data: false,
          hasClass5Data: false
        };

        // Always try to load C2 data (should be available)
        try {
          results.c2Cadets = await ExcelService.parseExcelData(FILE_PATHS.EXCEL.C2_SCOREBOARD);
        } catch (err) {
          console.warn("C2 scoreboard data not available:", err);
        }

        // Try to load Class 3 data
        try {
          results.c3BeforeCallout = await ExcelService.parseExcelData(FILE_PATHS.EXCEL.C3_SCOREBOARD_BEFORE_CALLOUT);
          results.hasClass3Data = true;
        } catch (err) {
          console.warn("Class 3 before callout data not available:", err);
        }

        // Try to load Class 3 after callout data (may not exist yet)
        try {
          results.c3AfterCallout = await ExcelService.parseExcelData(FILE_PATHS.EXCEL.C3_SCOREBOARD_AFTER_CALLOUT);
        } catch (err) {
          console.warn("Class 3 after callout data not available - using Class 3 before callout data:", err);
          results.c3AfterCallout = results.c3BeforeCallout; // Fallback to before callout data
        }

        // Try to load Class 4 data (may not exist yet)
        try {
          results.c4BeforeCallout = await ExcelService.parseExcelData(FILE_PATHS.EXCEL.C4_SCOREBOARD_BEFORE_CALLOUT);
          results.hasClass4Data = true;
        } catch (err) {
          console.warn("Class 4 before callout data not available:", err);
        }

        // Try to load Class 5 data (may not exist yet)
        try {
          results.c5BeforeCallout = await ExcelService.parseExcelData(FILE_PATHS.EXCEL.C5_SCOREBOARD_BEFORE_CALLOUT);
          results.hasClass5Data = true;
        } catch (err) {
          console.warn("Class 5 before callout data not available:", err);
        }

        return results;
      };

      const { c2Cadets, c3BeforeCallout, c3AfterCallout, c4BeforeCallout, c5BeforeCallout, hasClass3Data, hasClass4Data, hasClass5Data } = await loadFilesSafely();

      // Update available classes state
      setAvailableClasses({ hasClass3Data, hasClass4Data, hasClass5Data });

      // Only process data for available classes
      let finalScoreboardData = c2Cadets;
      let c3CalloutBaseData: Cadet[] = [];
      let class3Pairs: CalloutPair[] = [];
      let class4Pairs: CalloutPair[] = [];
      let c4AfterCalloutResults: Cadet[] = [];
      let c5BaseData: Cadet[] = [];
      let class5Pairs: CalloutPair[] = [];
      let c5AfterCalloutResults: Cadet[] = [];

      // Process Class 3 data if available
      if (hasClass3Data && c3BeforeCallout.length > 0) {
        // IMPORTANT: Class 3 callout pairings should be based ONLY on standings up to Class 3
        // We must NOT let Class 4 disqualifications affect Class 3 callout pairings
        
        // Merge C2 data with C3 data to create complete cadet records for Class 3 callout
        const mergedC3Data = c2Cadets.map((c2Cadet) => {
          const c3Data = c3BeforeCallout.find((c3) => c3.primaryCadet === c2Cadet.primaryCadet);
          if (c3Data && c3Data.c3) {
            // Create merged cadet with C3 data and updated total including C3 cycle
            const totalWithC3 = c2Cadet.total + (c3Data.c3.total || 0);
            const updatedCadet = {
              ...c2Cadet,
              c3: c3Data.c3,
              total: totalWithC3, // Update total to include C3 cycle performance
            };
            
            // Calculate disqualification status based ONLY on data up to Class 2
            // Class 3 callout includes anyone qualified through Class 2, even if they become DQ'd in Class 3
            updatedCadet.isDisqualified = ExcelService.calculateDisqualificationStatusUpTo(updatedCadet, 2);
            
            return updatedCadet;
          }
          return c2Cadet;
        });
        
        // Generate Class 3 callout pairs based on standings after Class 3 ONLY
        class3Pairs = await Class3CalloutUtils.generateClass3CalloutPairs(mergedC3Data);
        
        // Automatically process the callout results on load
        const processedC3Results = Class3CalloutUtils.processClass3CalloutResults(mergedC3Data, class3Pairs);
        
        // For the Class 3 callout tab, we need the merged data (C2 standings + C3 performance data)
        c3CalloutBaseData = mergedC3Data;
        
        // Set the Class 3 after callout results for Class 4 pairing generation
        finalScoreboardData = processedC3Results;
        
        // If we have Class 3 after callout data from file, use it as the authoritative source
        if (c3AfterCallout.length > 0 && c3AfterCallout !== c3BeforeCallout) {
          finalScoreboardData = c3AfterCallout;
        }
      }

      // Process Class 4 data if available  
      if (hasClass4Data && c4BeforeCallout.length > 0 && finalScoreboardData.length > 0) {
        // Class 4 callout pairings should be based on Class 3 after-callout results
        class4Pairs = await Class4CalloutUtils.generateClass4CalloutPairs(finalScoreboardData);
        c4AfterCalloutResults = Class4CalloutUtils.processClass4CalloutResults(c4BeforeCallout, class4Pairs);
        
        // Update main scoreboard to show Class 4 after-callout results
        finalScoreboardData = c4AfterCalloutResults;
      }

      // Process Class 5 data if available
      if (hasClass5Data && c5BeforeCallout.length > 0 && c4AfterCalloutResults.length > 0) {
        // Class 5 callout pairings should be based on Class 4 after-callout results
        class5Pairs = Class5CalloutUtils.generateClass5CalloutPairs(c4AfterCalloutResults);
        c5AfterCalloutResults = Class5CalloutUtils.processClass5CalloutResults(c5BeforeCallout, class5Pairs);
        
        // Update main scoreboard to show Class 5 after-callout results (final results)
        finalScoreboardData = c5AfterCalloutResults;
        // console.log('Final scoreboard data after Class 5 callout processing:', finalScoreboardData);
        
        // Set c5BaseData for the Class 5 callout tab (shows standings before Class 5 callout)
        c5BaseData = c4AfterCalloutResults;
      }
      
      // Set all the data
      setC2ScoreboardData(finalScoreboardData); // Main scoreboard shows the most current results
      setC3BeforeCalloutData(c3CalloutBaseData); // Data for Class 3 callout tab
      setC3AfterCalloutData(c3AfterCallout); // Pairing data for Class 4
      setC4BeforeCalloutData(c4BeforeCallout); // Processing data for Class 4
      setC4AfterCalloutData(c5BaseData); // Pairing data for Class 5
      setC5BeforeCalloutData(c5BeforeCallout); // Processing data for Class 5
      setClass3CalloutPairs(class3Pairs);
      setClass4CalloutPairs(class4Pairs);
      setClass5CalloutPairs(class5Pairs);
      setC5AfterCalloutData(c5AfterCalloutResults);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : UI_CONSTANTS.ERROR_MESSAGES.FETCH_FAILED;
      setError(errorMessage);
      console.error("Failed to load initial scoreboard data:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);


  const updateData = useCallback((data: Cadet[]) => {
    setC2ScoreboardData(data);
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return { 
    c2ScoreboardData, 
    c3BeforeCalloutData,
    c3AfterCalloutData,
    c4BeforeCalloutData,
    c4AfterCalloutData,
    c5BeforeCalloutData,
    c5AfterCalloutData,
    class3CalloutPairs,
    class4CalloutPairs, 
    class5CalloutPairs,
    availableClasses,
    isLoading, 
    error, 
    updateData, 
    refetch: loadInitialData 
  };
};

// Tab configuration
const TAB_CONFIG = [
  { label: 'Scoreboard', id: 'scoreboard' },
  { label: 'Class 3 Callout', id: 'class3-callout' },
  { label: 'Class 4 Callout', id: 'class4-callout' },
  { label: 'Class 5 Callout', id: 'class5-callout' }
] as const;

export const CombinedScoreboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const {
    c2ScoreboardData, 
    c3BeforeCalloutData,
    c3AfterCalloutData,
    c4BeforeCalloutData,
    c4AfterCalloutData,
    c5BeforeCalloutData,
    class3CalloutPairs,
    class4CalloutPairs, 
    class5CalloutPairs,
    availableClasses,
    isLoading, 
    error, 
    updateData, 
    refetch 
  } = useScoreboardData();

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  }, []);

  // Memoized tab content with availability checks
  const tabContent = useMemo(() => [
    <Scoreboard 
      key="scoreboard"
      data={c2ScoreboardData} 
    />,
    availableClasses.hasClass3Data ? (
      <Class3CalloutPairings 
        key="class3-callout"
        scoreboardData={c3BeforeCalloutData}
        onCalloutComplete={updateData}
        preloadedCalloutPairs={class3CalloutPairs}
      />
    ) : (
      <Box key="class3-unavailable" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Class 3 Data Not Available</Typography>
        <Typography variant="body1">Class 3 callout data has not been loaded yet. Please check back later.</Typography>
      </Box>
    ),
    availableClasses.hasClass4Data ? (
      <Class4CalloutPairings 
        key="class4-callout"
        scoreboardData={c4BeforeCalloutData}
        onCalloutComplete={updateData}
        preloadedCalloutPairs={class4CalloutPairs}
      />
    ) : (
      <Box key="class4-unavailable" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Class 4 Data Not Available</Typography>
        <Typography variant="body1">Class 4 callout data has not been loaded yet. Please check back later.</Typography>
      </Box>
    ),
    availableClasses.hasClass5Data ? (
      <Class5CalloutPairings 
        key="class5-callout"
        scoreboardData={c5BeforeCalloutData}
        onCalloutComplete={updateData}
        preloadedCalloutPairs={class5CalloutPairs}
      />
    ) : (
      <Box key="class5-unavailable" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Class 5 Data Not Available</Typography>
        <Typography variant="body1">Class 5 callout data has not been loaded yet. Please check back later.</Typography>
      </Box>
    )
  ], [c2ScoreboardData, c3BeforeCalloutData, c3AfterCalloutData, c4BeforeCalloutData, c4AfterCalloutData, c5BeforeCalloutData, class3CalloutPairs, class4CalloutPairs, availableClasses, updateData]);

  if (isLoading) {
    return <LoadingSpinner message={UI_CONSTANTS.LOADING_MESSAGES.SCOREBOARD_DATA} />;
  }

  if (error) {
    return <ErrorBoundary error={error} onRetry={refetch} />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          aria-label="scoreboard tabs"
        >
          {TAB_CONFIG.map((tab, index) => (
            <Tab 
              key={tab.id}
              label={tab.label} 
              id={`scoreboard-tab-${index}`}
              aria-controls={`scoreboard-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {TAB_CONFIG.map((_, index) => (
        <TabPanel key={index} value={currentTab} index={index}>
          {tabContent[index]}
        </TabPanel>
      ))}
    </Box>
  );
};
